/**
 * Estado de cuenta por unidad — función pura.
 *
 * Extrae la lógica per-apartamento que vive inline en
 * `src/screens/admin/CobranzaAdmin.jsx` (useMemo `unitData` + `stats`), para
 * poder reutilizarla en la vista "El edificio" del Estado de Cuenta del
 * residente (`src/components/BuildingAccountView.jsx`) SIN exponer nombres.
 *
 * Reglas de matching payment → unidad (idénticas a Cobranza):
 *   1. Si payment.apartmentId existe → matchea con esa unidad y solo esa.
 *   2. Si payment.unitId existe (manual) → matchea con esa unidad.
 *   3. Sin apartmentId/unitId → fallback heurístico por userid contra
 *      owner/tenant de la unidad, o el sintético `unit_<id>`.
 *
 * Deuda / crédito: mismo criterio que Cobranza (CobranzaAdmin.jsx:770-810):
 *   - totalDebt = Σ (remainingAmount || amount) de pagos !paid && status !== "rechazado"
 *     sobre los grupos owner + tenant + former (ocupante saliente).
 *   - totalCredit = saldo a favor DISPONIBLE (≤ 0): sobrepago (remaining negativo)
 *     + crédito ya aplicado a meses (paidFromCredit).
 *
 * Diferencia intencional con Cobranza: el vencido / `maxOverdue` se calcula
 * respetando el período de gracia del edificio (computeGraceEnd), igual que
 * kpiCalculations.getMaturityDate. Cobranza usa la maturity cruda; acá, si se
 * pasa `building`, quien paga dentro de la gracia NO figura vencido. Si no se
 * pasa `building`, se comporta como Cobranza (maturity cruda).
 */

import { toJSDate, computeGraceEnd } from "./interestCalculation";
import { esUnidadDelEdificio } from "./unidadesEdificio";

const pad = (n) => String(n).padStart(2, "0");

/**
 * Resuelve la fecha de vencimiento EFECTIVA de un pago (fin de gracia si hay
 * building). Replica kpiCalculations.getMaturityDate.
 * @returns {Date|null}
 */
function resolveEffectiveMaturity(payment, paymentDueDay, building) {
  let matIso = null;
  if (typeof payment.maturity === "string" && /^\d{4}-\d{2}-\d{2}/.test(payment.maturity)) {
    matIso = payment.maturity.slice(0, 10);
  } else {
    const mat = toJSDate(payment.maturity);
    if (mat) matIso = `${mat.getFullYear()}-${pad(mat.getMonth() + 1)}-${pad(mat.getDate())}`;
  }
  if (!matIso) {
    const m = Number(payment.month);
    const y = Number(payment.year);
    if (m && y) matIso = `${y}-${pad(m)}-${pad(paymentDueDay || 10)}`;
  }
  if (!matIso) return null;
  const effIso = building ? computeGraceEnd(matIso, building) : matIso;
  return toJSDate(effIso);
}

/**
 * Calcula el estado de cuenta de cada unidad del edificio.
 *
 * @param {Object}  params
 * @param {Array}   params.apartments - [{id, number, owner, ownerSecondary, tenant, tenantSecondary, unitKind}]
 * @param {Array}   params.payments   - payments de cierre del edificio (ya filtrados: montlyaccountId || isMigration)
 * @param {Object} [params.building]  - opcional; habilita el período de gracia en el cálculo de vencido
 * @returns {{
 *   units: Array<{ unitNumber:(string|number), unitId:string, totalDebt:number,
 *     totalCredit:number, maxOverdue:number, estado:("al_dia"|"en_deuda") }>,
 *   stats: { totalUnits:number, unitsInDebt:number, unitsAlDia:number,
 *     totalDebt:number, morosidad:number }
 * }}
 */
export function computeUnitsAccountStatus({ apartments = [], payments = [], building = null } = {}) {
  const now = new Date();
  const paymentDueDay = Number(building?.paymentDueDay) || 10;

  const units = apartments
    // R3: las unidades del edificio (portería, alquiler del edificio) no participan
    // del reparto de gastos comunes: no entran en la vista "El edificio".
    .filter((a) => !esUnidadDelEdificio(a))
    .map((apt) => {
      // 1) Payments de la unidad (mismo matching que Cobranza).
      const unitPayments = payments.filter((p) => {
        if (p.apartmentId) return p.apartmentId === apt.id;
        if (p.unitId) return p.unitId === apt.id;
        return (
          String(p.userid) === String(apt.owner) ||
          String(p.userid) === String(apt.tenant) ||
          p.userid === `unit_${apt.id}`
        );
      });

      // 2) Grupos owner / tenant / former (ocupante saliente).
      const ownerPayments = unitPayments.filter(
        (p) => String(p.userid) === String(apt.owner) || p.userid === `unit_${apt.id}`
      );
      const tenantPayments = unitPayments.filter(
        (p) => apt.tenant && String(p.userid) === String(apt.tenant)
      );
      const isCurrentOccupant = (uid) =>
        String(uid) === String(apt.owner) ||
        String(uid) === String(apt.ownerSecondary) ||
        String(uid) === String(apt.tenant) ||
        String(uid) === String(apt.tenantSecondary) ||
        uid === `unit_${apt.id}`;
      const formerPayments = unitPayments.filter((p) => !isCurrentOccupant(p.userid));

      // 3) Deuda impaga (bruta), por grupo (CobranzaAdmin.jsx:770-778).
      const sumDebt = (arr) =>
        arr
          .filter((p) => !p.paid && p.status !== "rechazado")
          .reduce((s, p) => s + (p.remainingAmount != null ? Number(p.remainingAmount) : (Number(p.amount) || 0)), 0);
      const totalDebt = sumDebt(ownerPayments) + sumDebt(tenantPayments) + sumDebt(formerPayments);

      // 4) Saldo a favor DISPONIBLE (≤ 0): sobrepago bruto − ya aplicado
      //    (CobranzaAdmin.jsx:784-795).
      const sumCredit = (arr) =>
        arr.reduce((s, p) => {
          const r =
            p.remainingAmount != null
              ? Number(p.remainingAmount)
              : (Number(p.amount) || 0) - (Number(p.paidAmount) || 0);
          const neg = r < 0 ? r : 0;
          const applied = Number(p.paidFromCredit) || 0;
          return s + neg + applied;
        }, 0);
      const totalCredit = sumCredit(ownerPayments) + sumCredit(tenantPayments) + sumCredit(formerPayments);

      // 5) Máximo atraso (días desde el fin de gracia del pago impago más
      //    antiguo). Solo cuentan los pagos que también suman deuda.
      let maxOverdue = 0;
      unitPayments
        .filter((p) => !p.paid && p.status !== "rechazado")
        .forEach((p) => {
          const matDate = resolveEffectiveMaturity(p, paymentDueDay, building);
          if (matDate && !isNaN(matDate.getTime())) {
            const diff = Math.floor((now - matDate) / (1000 * 60 * 60 * 24));
            if (diff > maxOverdue) maxOverdue = diff;
          }
        });

      return {
        unitNumber: apt.number || apt.id,
        unitId: apt.id,
        totalDebt,
        totalCredit,
        maxOverdue,
        estado: totalDebt > 0 ? "en_deuda" : "al_dia",
      };
    })
    // En deuda primero, luego por número de unidad (numérico).
    .sort((a, b) => {
      if ((a.totalDebt > 0) !== (b.totalDebt > 0)) return b.totalDebt > 0 ? 1 : -1;
      return String(a.unitNumber || "").localeCompare(String(b.unitNumber || ""), undefined, { numeric: true });
    });

  const totalUnits = units.length;
  const unitsInDebt = units.filter((u) => u.totalDebt > 0).length;
  const unitsAlDia = totalUnits - unitsInDebt;
  const totalDebt = units.reduce((s, u) => s + u.totalDebt, 0);
  const unitsOverdue = units.filter((u) => u.maxOverdue > 0).length;
  const morosidad = totalUnits > 0 ? Math.round((unitsOverdue / totalUnits) * 100) : 0;

  return {
    units,
    stats: { totalUnits, unitsInDebt, unitsAlDia, totalDebt, morosidad },
  };
}

export default computeUnitsAccountStatus;
